import { Typography } from "@circle-react-shared/uikit/Typography";
import type { PaywallPriceForm } from "@circle-react/components/Paywalls/Admin/PaywallsAdmin/types/PaywallPriceForm";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { PriceCallout as PriceCalloutType } from "@circle-react/types/PaywallCustomization";
import type { PropsWithChildren } from "react";
import { useMemo } from "react";

import { t } from "@/i18n-js/instance";

import { usePaywallCustomizationContext } from "../PaywallCustomizationProvider";
import { getBestFontColor } from "./getBestFontColor";

interface PriceCalloutProps {
  price: Pick<PaywallPriceForm, "id" | "tmp_id">;
}

export const PriceCallout = ({
  price,
  children,
}: PropsWithChildren<PriceCalloutProps>) => {
  const { paywallCustomization } = usePaywallCustomizationContext();
  const priceCalloutProperties = paywallCustomization?.price_callout_properties;

  const bestColorBaseByBackground = useMemo(
    () => getBestFontColor(priceCalloutProperties?.callout_color),
    [priceCalloutProperties?.callout_color],
  );

  const earlyReturn = <>{children}</>;
  if (
    !paywallCustomization ||
    !priceCalloutProperties ||
    !paywallCustomization?.price_callout_properties?.prices ||
    !priceCalloutProperties?.callout_color
  ) {
    return earlyReturn;
  }

  const priceCallouts = paywallCustomization.price_callout_properties.prices;
  const priceCallout = priceCallouts.find(
    (priceCalloutIterator: PriceCalloutType) =>
      priceCalloutIterator?.price_id &&
      [price.id, price.tmp_id].includes(priceCalloutIterator?.price_id),
  );

  if (!priceCallout || !priceCallout.callout_text) {
    return earlyReturn;
  }

  const isHighlighted = priceCallout.highlighted;

  return (
    <div className="flex flex-row flex-wrap items-center gap-x-1">
      <div className="flex flex-row items-center gap-x-1">
        {children}
        <Typography.LabelXs color="text-default">
          {t("separator_dot")}
        </Typography.LabelXs>
      </div>
      <Typography.LabelXs color="text-default">
        <span
          className={classNames("inline-block whitespace-nowrap", {
            "rounded-sm px-1.5 py-0.5": isHighlighted,
          })}
          style={{
            color: isHighlighted
              ? bestColorBaseByBackground
              : priceCalloutProperties.callout_color,
            backgroundColor: isHighlighted
              ? priceCalloutProperties.callout_color
              : "transparent",
          }}
          data-testid="paywall-price-callout"
        >
          {priceCallout.callout_text}
        </span>
      </Typography.LabelXs>
    </div>
  );
};
